import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MeResponse, Permission, Role } from '@homework/shared';
import { api, refreshSession, setAccessToken } from './api';
import { forgetCarriedPosition } from '../office/carriedPosition';

interface AuthContextValue {
  me: MeResponse | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  /** Reloads the profile after a change (role, integration, preference). */
  reload: () => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: Permission) => boolean;
  hasRole: (...roles: Role[]) => boolean;
  patchMe: (patch: Partial<MeResponse>) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>('loading');

  const load = useCallback(async () => {
    try {
      const profile = await api.get<MeResponse>('/me');
      setMe(profile);
      setStatus('authenticated');
    } catch {
      setMe(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void (async () => {
      // A page load starts with no in-memory token: try the refresh cookie first.
      await refreshSession();
      await load();
    })();
  }, [load]);

  // Keep the session alive while a tab stays open for a whole workday.
  useEffect(() => {
    if (status !== 'authenticated') return;
    const interval = setInterval(() => void refreshSession(), 10 * 60_000);
    return () => clearInterval(interval);
  }, [status]);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      setAccessToken(null);
      setMe(null);
      setStatus('anonymous');
      // Signing out is leaving: the next sign-in starts at the desk, even in
      // this same tab.
      forgetCarriedPosition();
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      status,
      reload: load,
      logout,
      can: (permission) => Boolean(me?.permissions.includes(permission)),
      hasRole: (...roles) => Boolean(me?.user.roles.some((role) => roles.includes(role))),
      patchMe: (patch) => setMe((previous) => (previous ? { ...previous, ...patch } : previous)),
    }),
    [me, status, load, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

/** Convenience for components that only render once authenticated. */
export function useMe(): MeResponse {
  const { me } = useAuth();
  if (!me) throw new Error('useMe used outside an authenticated route');
  return me;
}
